import React, { useRef, useEffect, useState } from 'react';
import BackButton from '../../../component/backButton';
import logo from '../../../assets/png/logoTransperent.png';
import CustomButton from '../../../component/elements/CustomButton/CustomButton';

const VerifyOtp = ({ otp, setOtp, onVerify, onResend, handleBack, loginContent }) => {
    const inputRefs = useRef([]);
    const [timer, setTimer] = useState(30);
    const [isResendActive, setIsResendActive] = useState(false);

    useEffect(() => {
        if (timer > 0) {
            const countdown = setTimeout(() => setTimer(timer - 1), 1000);
            return () => clearTimeout(countdown);
        } else {
            setIsResendActive(true);
        }
    }, [timer]);

    const handleChange = (value, index) => {
        if (!/^\d?$/.test(value)) return;
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        if (value && index < 5) {
            inputRefs.current[index + 1].focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    const handleResend = () => {
        if (!isResendActive) return;
        setTimer(30);
        setIsResendActive(false);
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
        onResend(loginContent);
    };

    const handlePaste = (e) => {
        const pastedData = e.clipboardData.getData('Text').trim();
        if (!/^\d{6}$/.test(pastedData)) return;
        const newOtp = pastedData.split('');
        setOtp(newOtp);
        const lastIndex = newOtp.findIndex((v) => !v);
        const nextIndex = lastIndex === -1 ? 5 : lastIndex;
        inputRefs.current[nextIndex]?.focus();
        e.preventDefault();
    };

    return (
        <div className="w-full max-w-[440px] mx-auto bg-white rounded-[32px] shadow-[0_20px_50px_rgba(0,0,0,0.1)] px-10 py-12 border border-gray-50">
            <div className="flex flex-col items-center">
                {/* Header Actions */}
                <div className="w-full flex justify-between items-center mb-6">
                    <BackButton onClick={() => handleBack()} className="hover:bg-gray-100 p-2 rounded-full transition-colors" />
                    <img src={logo} alt="TYDII Logo" className="h-12 w-auto" />
                    <div className="w-8" /> {/* Spacer for centering logo */}
                </div>

                {/* Text Content */}
                <div className="text-center mb-10">
                    <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Verify Account</h2>
                    <p className="text-gray-500 mt-3 font-medium leading-relaxed">
                        We've sent a 6-digit code to your <span className="text-gray-900 font-semibold">{loginContent?.type === 'email' ? 'Email' : 'Mobile'}</span>.
                    </p>
                </div>

                {/* OTP Input Grid */}
                <div className="flex justify-between w-full gap-2.5 mb-8">
                    {Array(6)
                        .fill('')
                        .map((_, index) => (
                            <input
                                key={index}
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                value={otp[index] || ''}
                                onChange={(e) => handleChange(e.target.value, index)}
                                onKeyDown={(e) => handleKeyDown(e, index)}
                                onPaste={index === 0 ? handlePaste : undefined}
                                ref={(el) => (inputRefs.current[index] = el)}
                                className="w-12 h-16 text-center border-2 border-gray-100 rounded-2xl text-2xl font-bold text-gray-800 focus:border-[#1876A9] focus:ring-4 focus:ring-[#1876A9]/10 outline-none transition-all duration-200"
                            />
                        ))}
                </div>

                {/* Resend Logic UI */}
                <div className="flex items-center justify-center space-x-2 mb-10">
                    {isResendActive ? (
                        <button
                            onClick={handleResend}
                            className="text-[#1876A9] font-bold text-sm hover:text-[#126197] hover:underline underline-offset-4 transition-all"
                        >
                            Resend Code
                        </button>
                    ) : (
                        <div className="flex items-center text-gray-400 text-sm font-medium bg-gray-50 px-4 py-2 rounded-full">
                            <span>Resend in</span>
                            <span className="ml-2 font-bold text-[#1876A9] tabular-nums">{timer}s</span>
                        </div>
                    )}
                </div>

                {/* Verification Button */}
                <CustomButton
                    onClick={onVerify}
                    customeClass={`w-full h-[56px] !rounded-2xl text-lg font-bold shadow-lg shadow-blue-900/10 active:scale-[0.98] transition-all ${
                        otp.join('').length < 6 
                        ? 'opacity-40 grayscale cursor-not-allowed shadow-none' 
                        : 'hover:shadow-blue-900/20'
                    }`}
                    disabled={otp.join('').length < 6}
                >
                    Verify & Continue
                </CustomButton>
                
                <p className="mt-6 text-xs text-gray-400 font-medium uppercase tracking-widest text-center">
                    Secure Verification by TYDII
                </p>
            </div>
        </div>
    );
};

export default VerifyOtp;
